import React, { useEffect, useState } from 'react';
import OrganizationContainer from '../../state/containers/OrganizationContainer';
import { Redirect } from 'react-router';
import AuthContainer from '../../state/containers/AuthContainer';
import Loader from '../../components/general/Loader';
import ApplicationContainer from '../../state/containers/ApplicationContainer';
import FeatureContainer from '../../state/containers/FeatureContainer';
import NotificationsContainer from '../../hooks/NotificationsContainer';
import LanguageContainer from '../../hooks/LanguageContainer';

// responsbile for initial load and route set up
interface SplashProps {
    authContainer: AuthContainer
    orgContainer: OrganizationContainer
    appContainer: ApplicationContainer
    featureContainer: FeatureContainer
}
const Splash = (props: SplashProps) => {
    const [isLoading, setIsLoading] = useState(true);

    const languagesContainer = LanguageContainer.useContainer();

    useEffect(() => {
        let savedRedirectThisMount = false;

        const asyncLoad = async () => {
            // Save the current deep-link URL before any loading/redirecting happens.
            // Only do this when the access token is actually expired (not just when
            // system features haven't loaded yet) to avoid saving stale URLs on normal
            // cold-start loads.
            const fullPath = window.location.pathname + window.location.search;
            const accessTokenExpired = props.authContainer.state.expirationDate != null
                && props.authContainer.state.expirationDate < new Date();

            if (accessTokenExpired && fullPath.length > 1 && !fullPath.startsWith('/login') && !fullPath.startsWith('/orgs') && fullPath !== '/v' && fullPath !== "/v/logout") {
                if (!sessionStorage.getItem('VOICIFY_POST_LOGIN_REDIRECT')) {
                    sessionStorage.setItem('VOICIFY_POST_LOGIN_REDIRECT', fullPath);
                    savedRedirectThisMount = true;
                }
            }

            await props.authContainer.refreshToken();
            const orgsResultPromise = props.orgContainer.getOrganizations();
            await Promise.all([
                languagesContainer.loadLanguages(),
                props.featureContainer.getSystemFeatures(),
                orgsResultPromise,
            ]);
            const orgsResult = await orgsResultPromise;

            // If the user came from a deep link, auto-select an org so they
            // skip the org-selection screen and land directly at their URL.
            const postLoginRedirect = sessionStorage.getItem('VOICIFY_POST_LOGIN_REDIRECT');
            if (postLoginRedirect) {
                if (props.orgContainer.state.currentOrganization == null) {
                    // No org selected yet (e.g. signed out previously) - auto-select so
                    // the user skips the org selection screen. Login scene will redirect
                    // to postLoginRedirect after auth completes.
                    const orgList = (orgsResult.resultType === 'Ok' && orgsResult.data) ? orgsResult.data : props.orgContainer.state.organizations;
                    if (orgList && orgList.length > 0) {
                        const currentEmail = localStorage.getItem('VOICIFY_USER');
                        const lastOrgByUserRaw = localStorage.getItem('VOICIFY_LAST_ORG_BY_USER');
                        const lastOrgByUser = lastOrgByUserRaw ? JSON.parse(lastOrgByUserRaw) : null;
                        const storedOrg = (lastOrgByUser && lastOrgByUser.email === currentEmail)
                            ? JSON.parse(lastOrgByUser.org)
                            : null;
                        const targetOrg = (storedOrg && orgList.find(o => o.id === storedOrg.id))
                            ? storedOrg
                            : orgList[0];
                        if (targetOrg) {
                            await props.orgContainer.setCurrentOrgById(targetOrg.id);
                        }
                    }
                }
                // Clear the key in all cases - either the org was just auto-selected and
                // the browser URL is already the deep link (no redirect needed), or the
                // org was already set and we're about to render InnerDashboard at the
                // current URL directly.
                sessionStorage.removeItem('VOICIFY_POST_LOGIN_REDIRECT');
                savedRedirectThisMount = false;
            }

            setIsLoading(false);
        }
        asyncLoad();

        // Add cleanup to avoid Warning: Can't perform a React state update on an unmounted component.
        // Also clear the key if we saved it this mount but asyncLoad didn't finish (i.e. didn't clear it).
        return () => {
            if (savedRedirectThisMount) {
                sessionStorage.removeItem('VOICIFY_POST_LOGIN_REDIRECT');
            }
            setIsLoading(false);
        };
    }, []);

    if (isLoading) {
        return (
            <div>
                <Loader />
            </div>
        );
    }

    if (props.orgContainer.state.currentOrganization == null) {
        if (location.href != "/orgs") {
            return <Redirect to="/orgs" />
        }
    }

    return (
        <div>
            <Loader />
        </div>
    );

}

export default Splash;